(() => {
  const UI_CLASS = "ne-search-ui";
  const STORAGE_KEY = "searchHistory";
  const DESKTOP_BREAKPOINT = 960;

  function addDesktopSearchLabel(header, search) {
    const searchButton = header.querySelector(
      ".header__row-desktop .header__icon-touch--search .icon-button-header-search"
    );

    if (!searchButton || searchButton.querySelector(`.${UI_CLASS}__search-text`)) {
      return;
    }

    const label = document.createElement("span");
    label.className = `${UI_CLASS}__search-text`;
    label.textContent = search.dataset.searchLabel;
    searchButton.appendChild(label);
  }

  function enhanceDesktopNavigation(header, search) {
    const segment = header.querySelector(".header__row-segment-desktop.right");
    const list = segment?.querySelector(":scope > ul.header__links-list");

    if (!segment || !list || segment.querySelector(`.${UI_CLASS}__right-section-list-wrapper`)) {
      return;
    }

    const wrapper = document.createElement("div");
    wrapper.className = `${UI_CLASS}__right-section-list-wrapper`;
    list.before(wrapper);
    wrapper.appendChild(list);

    const arrowIcon = `
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M1.875 7.438 12 17.563 22.125 7.438" stroke="currentColor" stroke-width="2"></path>
      </svg>
    `;

    const previous = document.createElement("button");
    previous.type = "button";
    previous.className = `${UI_CLASS}__right-section-list-arrow-left ab__arrow hide`;
    previous.setAttribute("aria-label", search.dataset.previousItemsLabel);
    previous.innerHTML = arrowIcon;

    const next = document.createElement("button");
    next.type = "button";
    next.className = `${UI_CLASS}__right-section-list-arrow-right ab__arrow`;
    next.setAttribute("aria-label", search.dataset.nextItemsLabel);
    next.innerHTML = arrowIcon;

    wrapper.prepend(previous);
    wrapper.append(next);

    const updateArrows = () => {
      const canScroll = window.innerWidth >= DESKTOP_BREAKPOINT
        && wrapper.scrollWidth > wrapper.clientWidth + 1;
      const atStart = wrapper.scrollLeft <= 1;
      const atEnd = wrapper.scrollLeft + wrapper.clientWidth >= wrapper.scrollWidth - 1;

      wrapper.classList.toggle("is-ready", canScroll);
      previous.classList.toggle("hide", !canScroll || atStart);
      next.classList.toggle("hide", !canScroll || atEnd);
    };

    previous.addEventListener("click", () => {
      wrapper.scrollBy({ left: -wrapper.clientWidth, behavior: "smooth" });
    });
    next.addEventListener("click", () => {
      wrapper.scrollBy({ left: wrapper.clientWidth, behavior: "smooth" });
    });
    wrapper.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows, { passive: true });
    requestAnimationFrame(updateArrows);
  }

  function clearSearchHistory(history) {
    try {
      window.localStorage.setItem(STORAGE_KEY, "[]");
    } catch (error) {
      console.warn("Search history could not be cleared from localStorage.", error);
    }

    document.cookie = "history=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax";
    document.dispatchEvent(new CustomEvent("nl:search-history-clear"));

    const list = history.querySelector("[search-advance-history]");
    if (list) {
      list.replaceChildren();
    }
    history.classList.add("hidden");
  }

  function enhanceSearchHistory(search) {
    const history = search.querySelector(".s_r_history");
    const list = history?.querySelector("[search-advance-history]");

    if (!history || !list) {
      return;
    }

    const bar = search.querySelector(".quick-search__bar");
    if (bar && history.parentElement !== bar) {
      bar.appendChild(history);
    }

    if (history.dataset.neSearchUiEnhanced === "true") {
      return;
    }
    history.dataset.neSearchUiEnhanced = "true";

    if (!history.querySelector(`.${UI_CLASS}__clear-all-cookie-btn`)) {
      const clearButton = document.createElement("button");
      clearButton.type = "button";
      clearButton.className = `${UI_CLASS}__clear-all-cookie-btn`;
      clearButton.innerHTML = `
        <span class="ab__btn-title"></span>
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" aria-hidden="true">
          <rect x="1.914" y="1.293" width="13" height="2" rx="1" transform="rotate(45 1.914 1.293)" fill="#919191"></rect>
          <rect x=".914" y="10.293" width="13" height="2" rx="1" transform="rotate(-45 .914 10.293)" fill="#919191"></rect>
        </svg>
      `;
      clearButton.querySelector(".ab__btn-title").textContent = search.dataset.clearHistoryLabel;
      clearButton.addEventListener("click", () => clearSearchHistory(history));
      history.appendChild(clearButton);
    }

    const updateHistoryHeight = () => {
      const height = list.getBoundingClientRect().height;
      if (height > 40) {
        history.style.setProperty("max-height", `${Math.ceil(height)}px`);
      } else {
        history.style.removeProperty("max-height");
      }
    };

    new MutationObserver(updateHistoryHeight).observe(list, { childList: true });
    updateHistoryHeight();
  }

  function enhanceZeroResults(search) {
    const message = search.querySelector(".qs_not_found:not(.hidden) .qs_not_found_text");
    const bar = search.querySelector(".quick-search__bar");

    if (!message || !bar) {
      return;
    }

    if (message.parentElement !== bar) {
      bar.appendChild(message);
    }
  }

  function moveResultsButton(search) {
    const resultGroup = search.querySelector(".qs_r_all");
    const submitButton = resultGroup?.querySelector(".qs_r_all_left > button[type='submit']");
    const rightColumn = resultGroup?.querySelector(".qs_r_all_right");

    if (submitButton && rightColumn && submitButton.previousElementSibling !== rightColumn) {
      rightColumn.after(submitButton);
    }
  }

  function enhanceSearch(search) {
    let updateScheduled = false;
    const update = () => {
      if (updateScheduled) {
        return;
      }

      updateScheduled = true;
      requestAnimationFrame(() => {
        updateScheduled = false;
        enhanceSearchHistory(search);
        enhanceZeroResults(search);
        moveResultsButton(search);
      });
    };

    const content = search.querySelector(".search_container");
    if (content) {
      new MutationObserver(update).observe(content, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"]
      });
    }

    search.addEventListener("click", (event) => {
      if (event.target.closest(".quick-search__close, [data-close-icon]")) {
        search.querySelector(".qs_not_found")?.classList.add("hidden");
      }
    });

    update();
  }

  function init() {
    const header = document.querySelector(".header__inner");
    const search = document.querySelector("#MainQuickSearch");

    if (!header || !search) {
      return;
    }

    document.body.classList.add(UI_CLASS);
    addDesktopSearchLabel(header, search);
    enhanceDesktopNavigation(header, search);
    enhanceSearch(search);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
